import React from "react";
import Frame from "../shell/Frame";
import Meta from "../shell/Meta";
import Metrics from "../parts/Metrics";
import Sectors from "../parts/Sectors";
import Invite from "../parts/Invite";
import brand from "../content/brand";

const About = () => (
  <Frame>
    <Meta title="About" description={`${brand.name} — engineering-led technology and internet services firm based in ${brand.hq.city}, ${brand.hq.country}.`} />

    <section className="svc-hero">
      <div className="shell">
        <div className="crumb">~ <span className="sep">/</span> about</div>
        <span className="eyebrow">About BTCRuka</span>
        <h1>An engineering studio for the technology backbone of ambitious businesses.</h1>
        <p className="lede">
          BTCRuka is built around senior engineers who have shipped and operated the systems they advise on. Every engagement
          is owned end-to-end by people who understand the trade-offs they are making — and write them down.
        </p>
      </div>
    </section>

    <Metrics />

    {/* Origin (duet) */}
    <section className="band">
      <div className="shell">
        <div className="duet">
          <div>
            <span className="eyebrow">Origin</span>
            <h2>Founded to do technology programmes properly.</h2>
            <p>
              BTCRuka was founded by engineers and delivery leaders who spent years watching the same programmes fail in the
              same predictable ways: undefined scope, vendor lock-in, junior teams shipping senior decisions, and reporting
              designed to obscure rather than inform.
            </p>
            <p>
              We built the firm we wished we could have hired in those situations. Senior practitioners across every
              discipline, an explicit refusal to take referral fees from software vendors, and contracts written around
              outcomes instead of activity.
            </p>
            <p>
              Today we serve customers across Northern Europe and the wider EMEA region — engagements ranging from a single
              CRM rollout to running the complete IT function of a mid-market business.
            </p>
          </div>
          <div className="terminal-block">
            <div className="head">
              <span>commitments.txt</span>
              <span className="badge">SIGNED</span>
            </div>
            <div className="grid2">
              <div className="cell">
                <div className="lbl">SENIOR-LED</div>
                <div className="v">10y+</div>
                <div className="n">Min. experience on every engagement</div>
              </div>
              <div className="cell">
                <div className="lbl">INDEPENDENT</div>
                <div className="v">0%</div>
                <div className="n">Referral fees from vendors</div>
              </div>
              <div className="cell">
                <div className="lbl">DOCUMENTED</div>
                <div className="v">100%</div>
                <div className="n">Engagements with handover docs</div>
              </div>
              <div className="cell">
                <div className="lbl">MEASURED</div>
                <div className="v">SLAs</div>
                <div className="n">Response · resolution · uptime · CSAT</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Commitments */}
    <section className="band band--lines">
      <div className="shell">
        <div className="section-head">
          <div className="left">
            <span className="eyebrow">How we work</span>
            <h2>Four commitments we put in writing.</h2>
            <p>Named clauses in every retainer and project contract we sign. If we breach one, you have a written right to terminate without penalty.</p>
          </div>
        </div>
        <div className="caps">
          {[
            { t: "Senior-led delivery", d: "Every engagement is owned by an engineer or PM with at least ten years in the field. Junior staff support, not lead." },
            { t: "Vendor-independent advice", d: "We accept no referral commissions from software, hardware or hosting vendors. The recommendation you get is the one we believe in." },
            { t: "Documented handover", d: "Everything we build is documented to the level a competent successor team could take over without a phone call back to us." },
            { t: "Measurable SLAs", d: "Response time, resolution time, uptime, customer satisfaction — written into every managed-service contract and reported every month." }
          ].map((c, i) => (
            <div key={c.t} className="cap" style={{ pointerEvents: "none" }}>
              <div className="code">COMMITMENT / {String(i + 1).padStart(2, "0")}</div>
              <h3>{c.t}</h3>
              <p>{c.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Sectors */}
    <section className="band">
      <div className="shell">
        <div className="section-head">
          <div className="left">
            <span className="eyebrow">Industries</span>
            <h2>Context, not just credentials.</h2>
            <p>Our engineers have built and operated systems inside each of the sectors below — we bring that context to the first conversation.</p>
          </div>
        </div>
        <Sectors />
      </div>
    </section>

    <Invite
      eyebrow="Work with us"
      title="A short conversation is enough to know if we are the right fit."
      text="Send us a brief on what you are trying to change. We respond within one business day with a clear, honest next step."
    />
  </Frame>
);

export default About;
